import { Button, makeStyles } from "@material-ui/core";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useState } from "react";

import { Organization } from "../../../../dtos/organization";
import { AjaxResult } from "../../../../enums/ajaxResult";
import { FormField } from "../../../shared/formField";
import { TextField } from "../../../shared/TextField";
import { ValidationErrors } from "../../../shared/validationErrors";
import { useApi } from "../../../useApi";

interface Props {
    organization: Organization;
    onEditSuccess: () => void;
    disabled: boolean;
}

export const OrganizationEditForm = ({
    organization,
    onEditSuccess,
    disabled,
}: Props) => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const { orgApi } = useApi();
    const [loading, setLoading] = useState(false);

    const [organizationEdit, setOrganizationEdit] = useState(organization);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    useEffect(() => {
        setOrganizationEdit(organization);
    }, [organization]);

    const handleSave = useCallback(() => {
        if (!organization) return;
        setValidationErrors([]);
        setLoading(true);
        orgApi.updateOrg(organization.id, { ...organizationEdit }).then(r => {
            if (r.result === AjaxResult.Success) {
                enqueueSnackbar("Successfully updated organization configuration", { variant: "success" });
                onEditSuccess();
            } else {
                if (r.messages) {
                    setValidationErrors(r.messages);
                }
                enqueueSnackbar("Failed to update configuration for organization", { variant: "error" });
            }
            setLoading(false);
        });
    }, [orgApi, organizationEdit, organization, enqueueSnackbar, onEditSuccess]);

    const editDisabled = disabled || loading;

    return (
        <>
            {validationErrors.length > 0 && (
                <div><ValidationErrors errors={validationErrors} /></div>
            )}

            <FormField label="Organization Name" required>
                <TextField
                    placeholder="Organization Name"
                    value={organizationEdit.name}
                    onChange={(event) => setOrganizationEdit({ ...organizationEdit, name: event.currentTarget.value })}
                    InputProps={{
                        inputProps: { className: classes.input },
                    }}
                    disabled={editDisabled}
                />
            </FormField>

            <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={editDisabled || organizationEdit.name === ""}
                className={classes.saveButton}
            >
                {loading ? "Saving..." : "Save"}
            </Button>
        </>
    );
};

const useStyles = makeStyles({
    input: {
        padding: "5px",
        width: "100%",
        maxWidth: "400px",
    },
    saveButton: {
        margin: "20px 0px 20px 0px",
    },
});
